import { compareForSort, normalize } from '@sentra/contracts';

/**
 * The ad-hoc field names this terminal has used before, offered as an autocomplete.
 *
 * **Names, never values.** What is remembered is the label an operator typed — "talla de ropa",
 * "alergias" — and nothing else. A label is a description of a kind of fact; the fact itself is
 * personal data and is encrypted at rest by the server, so writing one into browser storage would
 * put plaintext personal data on a shared terminal's disk, which the security doctrine forbids
 * outright. That distinction is the whole of why this module exists and why it stores what it does.
 *
 * **It is local to the terminal, and that is a decision rather than a limitation.** The API offers
 * no "names used before" collection: an ad-hoc field belongs to one record, and a route that
 * returned every label in the installation would be a new disclosure surface for an advisory. What
 * an operator actually wants is consistency with what they themselves have been typing all shift,
 * which is exactly what a terminal remembers. The names on the record being amended are merged in by
 * the caller, so editing a record always offers its own labels back.
 *
 * **A storage that throws is a storage that remembers nothing.** Private browsing and a disabled
 * site-data setting both make `localStorage` throw on access, and an autocomplete is not worth a
 * blank screen.
 */

/** Where the names live. Namespaced like every other key this application writes. */
export const ADHOC_NAMES_STORAGE_KEY = 'sentra.adhocFieldNames';

/** How many are kept. Enough for a shift's vocabulary, small enough to stay a useful list. */
export const MAX_REMEMBERED_NAMES = 40;

/** The slice of `Storage` this module uses, so a test hands in a double rather than a whole DOM. */
export type NameStorage = Pick<Storage, 'getItem' | 'setItem'>;

function parse(raw: string | null): string[] {
  if (raw === null) {
    return [];
  }
  try {
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((entry) => typeof entry === 'string') : [];
  } catch {
    return [];
  }
}

/** What this terminal remembers, in the order a reader would scan them. */
export function rememberedNames(storage: NameStorage): string[] {
  try {
    return parse(storage.getItem(ADHOC_NAMES_STORAGE_KEY)).toSorted(compareForSort);
  } catch {
    return [];
  }
}

/**
 * Records the names a submission used, folding away spellings of the same label.
 *
 * Two names that `normalize` to the same string are the same label written twice — "Alergias" and
 * "alergias" — and offering both would make the autocomplete the cause of the inconsistency it
 * exists to prevent. The most recently used spelling wins, so correcting a label's capitalisation
 * once is enough.
 */
export function rememberNames(storage: NameStorage, used: readonly string[]): void {
  const kept = new Map<string, string>();
  for (const name of used) {
    const trimmed = name.trim();
    if (trimmed.length > 0) {
      kept.set(normalize(trimmed), trimmed);
    }
  }
  try {
    for (const name of parse(storage.getItem(ADHOC_NAMES_STORAGE_KEY))) {
      const folded = normalize(name);
      if (!kept.has(folded)) {
        kept.set(folded, name);
      }
    }
    storage.setItem(
      ADHOC_NAMES_STORAGE_KEY,
      JSON.stringify([...kept.values()].slice(0, MAX_REMEMBERED_NAMES)),
    );
  } catch {
    // A terminal that cannot remember still registers people. See the module note.
  }
}
